import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Skeleton } from "../../../ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "../../../ui/pagination";
import { ArrowLeft, Search, RefreshCw } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { toast } from "sonner";
import {
  fetchBlastById,
  fetchBlastAnalytics,
  fetchRecipients,
  type BlastRow,
  type BlastAnalytics,
  type RecipientRow,
  type RecipientStatus,
} from "../../../../lib/blastsEngageV1";

const channelClass = (c: BlastRow["channel"]) =>
  c === "email"
    ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200"
    : "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-200";

const blastStatusClass = (s: BlastRow["status"]) => {
  switch (s) {
    case "sent":
      return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200";
    case "sending":
      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200";
    case "scheduled":
      return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200";
    case "draft":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const statusBadge = (s: RecipientStatus) => {
  switch (s) {
    case "active":
      return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200";
    case "completed":
      return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200";
    case "exhausted":
      return "bg-muted text-muted-foreground border-border";
    case "unsubscribed":
      return "bg-red-100 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-200";
  }
};

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const lastEvent = (r: RecipientRow): { label: string; ts: string | null } => {
  if (r.clicked_at) return { label: "Clicked", ts: r.clicked_at };
  if (r.opened_at) return { label: "Opened", ts: r.opened_at };
  if (r.delivered_at) return { label: "Delivered", ts: r.delivered_at };
  if (r.step3_sent_at) return { label: "Sent (step 3)", ts: r.step3_sent_at };
  if (r.step2_sent_at) return { label: "Sent (step 2)", ts: r.step2_sent_at };
  if (r.step1_sent_at) return { label: "Sent (step 1)", ts: r.step1_sent_at };
  return { label: "Pending", ts: null };
};

const PAGE_SIZE = 20;

const MetricCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Card>
    <CardContent className="p-5">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

const OutreachDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<BlastRow | null>(null);
  const [analytics, setAnalytics] = useState<BlastAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [recipientLoading, setRecipientLoading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const loadCampaign = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!id) return;
      if (!opts.silent) setLoading(true);
      try {
        const [b, a] = await Promise.all([
          fetchBlastById(id),
          fetchBlastAnalytics(id).catch(() => null),
        ]);
        setCampaign(b);
        setAnalytics(a);
      } catch (e) {
        console.error("Failed to load campaign", e);
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    void loadCampaign();
  }, [loadCampaign]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadCampaign({ silent: true });
      toast.success("Analytics refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setRecipientLoading(true);
    fetchRecipients(id, { page, pageSize: PAGE_SIZE, search })
      .then(({ rows, total }) => {
        if (cancelled) return;
        setRecipients(rows);
        setRecipientTotal(total);
      })
      .catch((e) => console.error("Failed to load recipients", e))
      .finally(() => !cancelled && setRecipientLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, page, search]);

  const totalPages = Math.max(1, Math.ceil(recipientTotal / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const sentDate = useMemo(() => {
    if (!campaign) return "";
    const d = new Date(campaign.sent_at ?? campaign.created_at);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, [campaign]);

  if (loading) {
    return (
        <div className="px-6 md:px-10 py-10">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-6 h-8 w-72" />
          <Skeleton className="mt-2 h-4 w-56" />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-5 space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-16" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Skeleton className="mt-10 h-48 w-full" />
          <Skeleton className="mt-10 h-64 w-full" />
        </div>
    );
  }

  if (!campaign) {
    return (
        <div className="px-10 py-10">
          <Link to="/clientpanel/enagev1/outreach" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Review Outreach
          </Link>
          <h1 className="mt-6 text-2xl font-semibold">Campaign not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We couldn't find a campaign with that ID.
          </p>
        </div>
    );
  }

  const a = analytics ?? {
    recipients: campaign.recipients,
    delivered: campaign.delivered,
    opened: campaign.opened,
    clicked: campaign.clicked,
    deliveredPct: campaign.deliveredPct,
    openedPct: campaign.openedPct,
    clickedPct: campaign.clickedPct,
    perStep: [1, 2, 3].map((step) => ({ step, sent: 0, opened: 0, clicked: 0 })),
  };

  return (
      <div className="px-6 md:px-10 py-10">
        <Link to="/clientpanel/enagev1/outreach" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Review Outreach
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight">{campaign.name}</h1>
              <Badge variant="outline" className={blastStatusClass(campaign.status)}>
                {titleCase(campaign.status)}
              </Badge>
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              <span>{campaign.outlet_name}</span>
              <span>·</span>
              <Badge variant="outline" className={channelClass(campaign.channel)}>
                {campaign.channel === "sms" ? "SMS" : "Email"}
              </Badge>
              <span>·</span>
              {campaign.status === "scheduled" && campaign.scheduled_at ? (
                <span>Scheduled for {new Date(campaign.scheduled_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              ) : campaign.sent_at ? (
                <span>Sent {sentDate}</span>
              ) : campaign.status === "sending" ? (
                <span>Sending… created {sentDate}</span>
              ) : (
                <span>Created {sentDate}</span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh analytics"
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Metric cards */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard label="Total Sent" value={a.recipients.toLocaleString()} />
          <MetricCard
            label="Delivered"
            value={a.delivered.toLocaleString()}
            sub={`${a.deliveredPct.toFixed(1)}% of sent`}
          />
          <MetricCard
            label="Opened"
            value={
              campaign.channel === "sms"
                ? "N/A"
                : a.openedPct === null
                ? "—"
                : `${a.openedPct.toFixed(1)}%`
            }
            sub={campaign.channel === "email" ? "Email only" : "Not tracked for SMS"}
          />
          <MetricCard label="Clicked" value={`${a.clickedPct.toFixed(1)}%`} />
        </div>

        {/* Sequence timeline */}
        <div className="mt-10">
          <h2 className="text-lg font-semibold">Sequence</h2>
          <div className="mt-4 relative rounded-lg border bg-card p-6">
            <div className="absolute left-9 top-10 bottom-10 w-px bg-border" aria-hidden />
            <div className="space-y-6">
              {a.perStep.map((s, idx) => {
                const dayLabel = idx === 0 ? "Day 0 (Initial)" : idx === 1 ? "Day 7 (Follow-up)" : "Day 14 (Final)";
                const openRate = campaign.channel === "sms" ? null : (s.sent > 0 ? (s.opened / s.sent) * 100 : 0);
                const clickRate = s.sent > 0 ? (s.clicked / s.sent) * 100 : 0;
                return (
                  <div key={idx} className="relative flex items-start gap-4">
                    <div className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-background text-xs font-medium text-primary">
                      {idx + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-4 md:grid-cols-5">
                      <div>
                        <div className="text-sm font-medium">Step {s.step}</div>
                        <div className="text-xs text-muted-foreground">{dayLabel}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Sent</div>
                        <div className="text-sm font-medium">{s.sent.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Open rate</div>
                        <div className="text-sm font-medium">
                          {openRate === null ? "N/A" : `${openRate.toFixed(1)}%`}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Click rate</div>
                        <div className="text-sm font-medium">{clickRate.toFixed(1)}%</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Clicked</div>
                        <div className="text-sm font-medium">{s.clicked.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recipients */}
        <div className="mt-10">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold">Recipients</h2>
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search name or email"
                className="pl-9"
              />
            </div>
          </div>

          <div className="mt-4 rounded-lg border bg-card overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>{campaign.channel === "email" ? "Email" : "Phone"}</TableHead>
                  <TableHead>Step Reached</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Event</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipientLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">
                      Loading recipients…
                    </TableCell>
                  </TableRow>
                ) : recipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">
                      {search ? "No recipients match your search." : "No recipients yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  recipients.map((r) => {
                    const ev = lastEvent(r);
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.name || "—"}</TableCell>
                        <TableCell>
                          {(campaign.channel === "email" ? r.email : r.phone) || "—"}
                        </TableCell>
                        <TableCell>Step {r.current_step}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusBadge(r.sequence_status)}>
                            {titleCase(r.sequence_status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{ev.label}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {ev.ts ? new Date(ev.ts).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, recipientTotal)} of {recipientTotal}
              </div>
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <PaginationItem key={i}>
                      <PaginationLink
                        href="#"
                        isActive={i + 1 === safePage}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(i + 1);
                        }}
                      >
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.min(totalPages, p + 1));
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
  );
};

export default OutreachDetail;
